'use client';

import { useState, useCallback } from 'react';
import {
  Question,
  Gift,
  Percent,
  ChatCircle,
  CaretDown,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

// FAQ синхронизирован с мобилкой (sales.tsx).
// Упоминания «День рождения» убраны вместе со скрытием самой акции.
const FAQ_ITEMS = [
  {
    id: 1,
    question: 'Какие акции сейчас действуют?',
    answer:
      'У нас всегда действуют несколько выгодных акций: статусные роллы, подарок за первый заказ, ролл за отзыв. Подробности по каждой акции вы можете узнать на нашем сайте в разделе «Акции».',
    icon: Gift,
  },
  {
    id: 2,
    question: 'Можно ли совмещать акции?',
    answer:
      'Большинство наших акций можно совмещать с акцией «Статусные роллы». Информация о возможности совмещения указана в условиях каждой конкретной акции.',
    icon: Percent,
  },
  {
    id: 3,
    question: 'Как применить промокод?',
    answer:
      'В корзине нажмите на кнопку «У меня есть промокод». В открывшемся поле введите ваш промокод, и преимущества будут автоматически применены к вашему заказу.',
    icon: ChatCircle,
  },
];

export function ActionsFAQ() {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const toggle = useCallback((id: number) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  return (
    <section className="py-16 sm:py-24 bg-[var(--bg-secondary)] relative overflow-hidden">
      <div className="blur-blob absolute -top-20 -left-20 w-[400px] h-[400px] rounded-full bg-[var(--primary)]/6 blur-[100px]" />
      <div className="blur-blob absolute bottom-0 right-0 w-[300px] h-[300px] rounded-full bg-[var(--violet-400)]/5 blur-[80px]" />

      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10 sm:mb-14 animate-fade-up">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--primary-subtle)] text-[var(--primary)] text-sm font-semibold mb-4">
            <Question weight="fill" className="w-4 h-4" />
            FAQ
          </span>
          <h2 className="text-3xl sm:text-4xl font-heading font-black tracking-tight text-[var(--text-primary)] mb-4">
            Часто задаваемые{' '}
            <span className="text-[var(--primary)]">вопросы</span>
          </h2>
          <p className="text-[var(--text-secondary)] text-lg">
            Ответы на вопросы по акциям и специальным предложениям
          </p>
        </div>

        <div className="space-y-3">
          {FAQ_ITEMS.map((item) => {
            const isOpen = expandedId === item.id;
            return (
              <div
                key={item.id}
                className={cn(
                  'rounded-2xl border transition-all duration-200',
                  'bg-[var(--surface-primary)]',
                  isOpen
                    ? 'border-[var(--primary)]/25 shadow-lg shadow-[var(--primary)]/5'
                    : 'border-[var(--border-default)] hover:border-[var(--border-hover)]',
                )}
              >
                <button
                  type="button"
                  onClick={() => toggle(item.id)}
                  className="w-full flex items-center gap-4 p-5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]/30 rounded-2xl"
                  aria-expanded={isOpen}
                >
                  <div
                    className={cn(
                      'w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all duration-200',
                      isOpen
                        ? 'bg-[var(--primary)] shadow-md shadow-[var(--primary)]/20'
                        : 'bg-[var(--primary-subtle)]',
                    )}
                  >
                    <item.icon
                      weight="fill"
                      className={cn(
                        'w-5 h-5 transition-colors duration-200',
                        isOpen ? 'text-white' : 'text-[var(--primary)]',
                      )}
                    />
                  </div>

                  <span
                    className={cn(
                      'flex-1 text-sm sm:text-base font-bold transition-colors duration-200',
                      isOpen ? 'text-[var(--primary)]' : 'text-[var(--text-primary)]',
                    )}
                  >
                    {item.question}
                  </span>

                  <div
                    className={cn(
                      'w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-all duration-200',
                      isOpen ? 'bg-[var(--primary)] rotate-180' : 'bg-[var(--primary-subtle)]',
                    )}
                  >
                    <CaretDown
                      weight="bold"
                      className={cn(
                        'w-3.5 h-3.5 transition-colors duration-200',
                        isOpen ? 'text-white' : 'text-[var(--primary)]',
                      )}
                    />
                  </div>
                </button>

                {/* CSS grid for smooth height animation */}
                <div
                  className="grid transition-[grid-template-rows] duration-300 ease-out"
                  style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}
                >
                  <div className="overflow-hidden">
                    <div className="px-5 pb-5 pt-0">
                      <div className="pl-14 border-t border-[var(--primary)]/10 pt-4">
                        <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
                          {item.answer}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
